import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Link as LinkComponent } from 'react-router'
import {
  Alert,
  Stack,
  Tooltip,
  Typography,
  Link,
  MenuItem,
  FormControl,
  FormGroup,
  FormLabel,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { useUpdateProjectGridField } from '@/service-library/hooks/project-grid-fields'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { ProjectGridField } from '@/types/fields'
import PaginatedResponse from '@/types/paginated-response'
import { ProjectGrid } from '@/types/projects'
import getDataTypeIcon from '@/utils/data-type'
import {
  aggregateFunctionOptions,
  numberFieldTypes,
  validateFieldName,
} from '@/utils/field'
import { showErrorSnackbar } from '@/utils/snackbars'
import { notNull } from '@/utils/typescript'
import {
  FormMultiSelectCheckbox,
  FormSelect,
  FormTextField,
  PixyDocsForm,
} from '@/components/forms'
import LabeledData from '@/components/labeled-data/LabeledData'
import { useProjectGridsContext } from '@/components/project-settings/ProjectGridsProvider'
import SuperUserFieldInfo from '@/components/super-user-field-info/SuperUserFieldInfo'
import { useWorkflowsContext } from '@/components/workflows-provider/WorkflowsProvider'
import FormRadioGroup from '../forms/FormRadioGroup'
import FieldAIInstructions from './FieldAIInstructions'
import FieldSettingsFooter from './FieldSettingsFooter'
import LiveListFilterConfigSetting from './LiveListFilterConfigSetting'
import PickerListInfo from './PickerListInfo'
import useCSVHeaderField from './useCSVHeaderField'

type FieldSettingsProps = {
  field: ProjectGridField
  isSettings: boolean
  isTableField: boolean
  projectId: string
  orgCredentialTypeId: string | null
  projectGridFields: ProjectGridField[]
}

type SimplifiedInputBehavior = 'allow_chips' | 'allow_manual_value'

type FormValues = Omit<
  ProjectGridField,
  'included_grid_fields' | 'fields' | 'input_behavior'
> & {
  simplified_input_behavior: SimplifiedInputBehavior[]
  ask_on_import: ProjectGridField['ask_on_import']
  csv_header_name: string
}

function getSimplifiedInputBehavior(
  inputBehavior: ProjectGridField['input_behavior'],
): SimplifiedInputBehavior[] {
  switch (inputBehavior) {
    case 'manual_allowed':
      return ['allow_chips', 'allow_manual_value']
    case 'manual_not_allowed':
      return ['allow_chips']
    case 'manual_only':
      return ['allow_manual_value']
  }
}

function getInputBehavior(
  simplifiedInputBehavior: SimplifiedInputBehavior[],
): ProjectGridField['input_behavior'] {
  if (
    simplifiedInputBehavior.includes('allow_chips') &&
    simplifiedInputBehavior.includes('allow_manual_value')
  ) {
    return 'manual_allowed'
  }

  if (simplifiedInputBehavior.includes('allow_chips')) {
    return 'manual_not_allowed'
  }

  return 'manual_only'
}

const simplifiedInputBehaviorOptions: SimplifiedInputBehavior[] = [
  'allow_chips',
  'allow_manual_value',
]

export const FieldSettings = ({
  field,
  isTableField,
  projectId,
  orgCredentialTypeId,
  projectGridFields,
}: FieldSettingsProps) => {
  const isSuperUser = useIsSuperUser()
  const { workflows } = useWorkflowsContext()

  const fieldNamesToCheck = useMemo(
    () =>
      projectGridFields
        .map((gridField) =>
          gridField.id !== field.id &&
          gridField.project_grid_id === field.project_grid_id
            ? gridField.name.toLowerCase()
            : null,
        )
        .filter(notNull),
    [field.id, field.project_grid_id, projectGridFields],
  )

  const queryClient = useQueryClient()
  const { queryKey } = useProjectGridsContext()
  const { updateProjectGridField, isPending } = useUpdateProjectGridField({
    sideEffectQueryKeys: [
      queryKey,
      queryKeys.projects.details(),
      queryKeys.projectGrids.lists(),
    ],
    onMutate: (updatedField) => {
      queryClient.setQueryData<InfiniteData<PaginatedResponse<ProjectGrid>>>(
        queryKey,
        (oldData) => {
          const updatedPages = oldData?.pages.map((page) => {
            return {
              ...page,
              results: page.results.map((grid) => {
                if (grid.id === updatedField.project_grid_id) {
                  return {
                    ...grid,
                    project_grid_fields: grid.project_grid_fields.map(
                      (gridField) => {
                        if (gridField.id === updatedField.id) {
                          return updatedField
                        }
                        return gridField
                      },
                    ),
                  }
                }
                return grid
              }),
            }
          })
          return {
            pageParams: oldData?.pageParams || [],
            pages: updatedPages || [],
          }
        },
      )
    },
    onError: () => {
      showErrorSnackbar('Failed to update field. Please try again later.')
    },
  })

  const {
    csvHeaderValue = '',
    saveNameChange,
    isCsvHeaderLoading,
  } = useCSVHeaderField({ projectId, field })

  const defaultValues = useMemo<FormValues>(
    () => ({
      ...field,
      simplified_input_behavior: getSimplifiedInputBehavior(
        field.input_behavior,
      ),
      ask_on_import: field.ask_on_import,
      params: {
        ...field.params,
        default_prediction_context_columns:
          field.params?.default_prediction_context_columns || [], // We do this so dirty fields is not comparing undefined to []
      },
      csv_header_name: csvHeaderValue,
    }),
    [field, csvHeaderValue],
  )

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onChange', // triggers validation onChange
  })

  const {
    formState: { errors },
    reset,
    resetField,
    watch,
  } = methods

  const savedCsvHeaderName = watch('csv_header_name')
  const simplifiedInputBehavior = watch('simplified_input_behavior')

  const checkedCSVHeaderRef = useRef(false)
  useEffect(() => {
    if (
      !savedCsvHeaderName &&
      !!csvHeaderValue &&
      !checkedCSVHeaderRef.current
    ) {
      resetField('csv_header_name', { defaultValue: csvHeaderValue })
      checkedCSVHeaderRef.current = true
    }
  }, [csvHeaderValue, resetField, savedCsvHeaderName])

  function onSubmit(formValues: FormValues) {
    const { csv_header_name, ...rest } = formValues

    const updatedField = {
      ...field,
      ...rest,
      input_behavior: getInputBehavior(rest.simplified_input_behavior),
    }

    updateProjectGridField(updatedField)
    if (csv_header_name !== csvHeaderValue) {
      saveNameChange(csv_header_name)
    }

    reset({
      ...formValues,
      csv_header_name,
    })
  }

  const fieldCode = field.project_grid_field_type.code
  const Icon = getDataTypeIcon(fieldCode)
  const notGrid = fieldCode !== 'grid'

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  return (
    <PixyDocsForm
      methods={methods}
      onSubmit={onSubmit}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{ py: 2, flexGrow: 1 }}
      >
        <Stack sx={{ px: 2, pb: 2 }} spacing={3}>
          {field.has_category_model && (
            <Alert variant="outlined" severity="info">
              This field has a category model attached to it.
              {workflows.length > 0 && (
                <>
                  {' '}
                  <LinkComponent
                    to={`../../dashboard?workflow=${
                      workflows.find(
                        ({ project_model_id }) =>
                          project_model_id === field.project_model_id,
                      )?.id
                    }`}
                    target="_blank"
                  >
                    <Link component="span">Click here</Link>
                  </LinkComponent>{' '}
                  to view it.
                </>
              )}
            </Alert>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            {Icon ? (
              <Tooltip title={field.project_grid_field_type.name}>
                <Icon />
              </Tooltip>
            ) : null}
            <Typography variant="h6">Settings</Typography>
          </Stack>

          {isSuperUser && <SuperUserFieldInfo field={field} />}

          {isSuperUser && fieldCode === 'live-list-picker' && (
            <LiveListFilterConfigSetting
              field={field}
              orgCredentialTypeId={orgCredentialTypeId}
            />
          )}

          {/* MARK: Field Name */}
          <FormTextField
            name="name"
            label="Name"
            autoFocus
            error={!!errors.name}
            helperText="Must be unique."
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            sx={{ maxWidth: 300 }}
            required
            useControllerProps={{
              rules: {
                validate: (value) =>
                  validateFieldName(value, fieldNamesToCheck),
              },
            }}
          />

          {field.info_type && (
            <LabeledData label="Info Type" data={field.info_type.name} />
          )}

          <FieldAIInstructions field={field} />

          {/* MARK: Picker Columns */}
          {fieldCode === 'picker' && (
            <PickerListInfo
              // @ts-expect-error -- We know this field will have the needed data at this point
              field={field}
            />
          )}

          {/* MARK: Aggregate Function for number fields */}
          {isTableField && numberFieldTypes.includes(fieldCode) && (
            <FormSelect
              name="aggregate_function"
              label="Show Aggregate"
              helperText="If set, shows the result of the selected aggregate function at the bottom of the table."
              sx={{ maxWidth: 300 }}
              variant="filled"
            >
              {aggregateFunctionOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </FormSelect>
          )}

          {/* MARK: Input Behavior for Manual Values */}
          {notGrid && fieldCode !== 'live-list-picker' && (
            <FormControl sx={{ rowGap: 1, maxWidth: 350 }}>
              <FormLabel component="legend">Input Behavior</FormLabel>
              <FormGroup>
                {simplifiedInputBehaviorOptions.map((option) => (
                  <FormMultiSelectCheckbox
                    key={option}
                    disabled={
                      simplifiedInputBehavior.length === 1 &&
                      simplifiedInputBehavior.includes(option)
                    }
                    name="simplified_input_behavior"
                    label={
                      option === 'allow_chips'
                        ? 'Allow assigning chips to field'
                        : 'Allow users to enter a manual value'
                    }
                    value={option}
                    sx={{ mt: -1 }}
                    formLabelSx={{
                      alignItems: 'flex-start',
                    }}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}

          {/* MARK: Ask on Import */}
          {notGrid && !isTableField && (
            <FormRadioGroup
              name="ask_on_import"
              label="Allow Setting Field Value on Upload"
            >
              <FormControlLabel
                value="optional"
                control={<Radio size="small" />}
                label="Ask, but don't require"
              />
              <FormControlLabel
                value="no"
                control={<Radio size="small" />}
                label="Don't allow"
              />
            </FormRadioGroup>
          )}

          {/* MARK: CSV Header Name */}
          {notGrid && (
            <FormTextField
              name="csv_header_name"
              label="CSV Header Name"
              disabled={isCsvHeaderLoading}
              sx={{ maxWidth: 300 }}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
        </Stack>
      </Stack>

      <FieldSettingsFooter field={field} isSaving={isPending} />
    </PixyDocsForm>
  )
}
